import * as React from 'react';
import { Redirect } from 'react-router-dom';
import driverinfosecurity from '../../utils/DriverInfoSecurity';
import * as i18next from 'i18next';
import DefaultPromptModal from '../Controls/DefaultPromptModal';
import { RouteComponentProps } from 'react-router';
import Spinner from '../Controls/Spinner';
import moment from 'moment';
import { useDocument, useAddDocument, useEditDocument, useDeleteDocument, useDocuments, DocumentViewModel } from '../../hooks/useDocuments';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid2';
import Autocomplete from '@mui/material/Autocomplete';
import UploadFileIcon from '@mui/icons-material/UploadFile';

function AdminDocumentView({ match }: RouteComponentProps<{ documentId: string }>) {
    const isAddMode = match.params.documentId === '0';
    const documentId = parseInt(match.params.documentId);

    const [title, setTitle] = React.useState('');
    const [titleError, setTitleError] = React.useState(false);
    const [description, setDescription] = React.useState('');
    const [descriptionError, setDescriptionError] = React.useState(false);
    const [carMake, setCarMake] = React.useState('');
    const [documentType, setDocumentType] = React.useState('');
    const [file, setFile] = React.useState<File | null>(null);
    const [showDeleteModal, setShowDeleteModal] = React.useState(false);
    const [showDeleteDoneModal, setShowDeleteDoneModal] = React.useState(false);
    const [goBack, setGoBack] = React.useState(false);
    const [showSavedMessage, setShowSavedMessage] = React.useState(false);

    const { data: document, isLoading } = useDocument(isAddMode ? 0 : documentId);
    const { data: allDocuments = [] } = useDocuments();
    const addDocumentMutation = useAddDocument();
    const editDocumentMutation = useEditDocument();
    const deleteDocumentMutation = useDeleteDocument();

    const carMakeOptions = React.useMemo(
        () => [...new Set(allDocuments.map(d => d.carMake).filter((m): m is string => !!m))],
        [allDocuments],
    );

    const documentTypeOptions = React.useMemo(
        () => [...new Set(allDocuments.map(d => d.documentType).filter((t): t is string => !!t))],
        [allDocuments],
    );

    React.useEffect(() => {
        if (document) {
            setTitle(document.title || '');
            setDescription(document.description || '');
            setCarMake(document.carMake || '');
            setDocumentType(document.documentType || '');
        }
    }, [document]);

    React.useEffect(() => {
        if (editDocumentMutation.isSuccess) setShowSavedMessage(true);
    }, [editDocumentMutation.isSuccess]);

    if (addDocumentMutation.isSuccess && addDocumentMutation.data) {
        return <Redirect to={'/administrationdocument/' + addDocumentMutation.data.documentId} />;
    }
    if (goBack) return <Redirect to={'/administrationdocuments'} />;
    if (isLoading) return <Spinner />;

    function openDocument(url: string) {
        window.open(url, '_blank');
    }

    function saveUpdateDocument(e: React.MouseEvent) {
        e.preventDefault();
        let hasError = false;
        if (!title) { setTitleError(true); hasError = true; } else { setTitleError(false); }
        if (!description) { setDescriptionError(true); hasError = true; } else { setDescriptionError(false); }
        if (hasError) return;

        if (driverinfosecurity.hasValidToken() && driverinfosecurity.isAdmin()) {
            if (isAddMode) {
                const docViewModel: DocumentViewModel = { documentId: 0, filename: file ? file.name : '', title, description, carMake: carMake || undefined, documentType: documentType || undefined };
                const formData = new FormData();
                if (file) formData.append('file', file);
                formData.append('fileInfo', JSON.stringify(docViewModel));
                addDocumentMutation.mutate(formData);
            } else {
                const docViewModel: DocumentViewModel = { documentId, filename: document ? document.filename : '', title, description, carMake: carMake || undefined, documentType: documentType || undefined };
                const formData = new FormData();
                formData.append('fileInfo', JSON.stringify(docViewModel));
                editDocumentMutation.mutate(formData);
            }
        }
    }

    function handleSubmitDeleteModal() {
        if (document) {
            deleteDocumentMutation.mutate(document.documentId, {
                onSuccess: () => { setShowDeleteModal(false); setShowDeleteDoneModal(true); }
            });
        }
    }

    return (
        <Box sx={{ maxWidth: 800 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
                {isAddMode ? i18next.t('documentadd.title') : i18next.t('documentedit.title')}
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {showSavedMessage && !isAddMode &&
                <Alert severity="success" sx={{ mb: 2 }}>{i18next.t('documentedit.messagesent')}</Alert>
            }

            <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Grid container alignItems="center" spacing={2}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Typography component="label" fontWeight={500}>{i18next.t('documentedit.file')}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 8 }}>
                        {isAddMode ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Button
                                    component="label"
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<UploadFileIcon />}
                                    htmlFor="file"
                                >
                                    {i18next.t('documentedit.choosefile')}
                                    <input
                                        id="file"
                                        type="file"
                                        style={{ display: 'none' }}
                                        onChange={(e) => setFile(e.target.files ? e.target.files[0] : null)}
                                    />
                                </Button>
                                <Typography variant="body2" color="text.secondary">
                                    {file ? file.name : i18next.t('documentedit.nofile')}
                                </Typography>
                            </Box>
                        ) : (
                            <Typography
                                component="a"
                                onClick={() => document && openDocument(document.documentUrl)}
                                sx={{ cursor: 'pointer', color: 'secondary.main' }}
                            >
                                {document ? document.filename : ''}
                            </Typography>
                        )}
                    </Grid>
                </Grid>

                <Grid container alignItems="center" spacing={2}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Typography component="label" htmlFor="title" fontWeight={500}>{i18next.t('documentedit.documenttitle')}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 8 }}>
                        <TextField
                            id="title"
                            fullWidth
                            size="small"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder={i18next.t('documentedit.documenttitle')}
                            error={titleError}
                            helperText={titleError ? i18next.t('documentedit.titleerror') : ''}
                        />
                    </Grid>
                </Grid>

                <Grid container alignItems="center" spacing={2}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Typography component="label" htmlFor="description" fontWeight={500}>{i18next.t('documentedit.documentdescription')}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 8 }}>
                        <TextField
                            id="description"
                            fullWidth
                            size="small"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder={i18next.t('documentedit.documentdescription')}
                            error={descriptionError}
                            helperText={descriptionError ? i18next.t('documentedit.descriptionerror') : ''}
                        />
                    </Grid>
                </Grid>

                <Grid container alignItems="center" spacing={2}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Typography component="label" fontWeight={500}>{i18next.t('documentedit.carMake')}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 8 }}>
                        <Autocomplete
                            freeSolo
                            options={carMakeOptions}
                            inputValue={carMake}
                            onInputChange={(_e, value) => setCarMake(value)}
                            onChange={(_e, value) => setCarMake(typeof value === 'string' ? value : '')}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    size="small"
                                    fullWidth
                                    label={i18next.t('documentedit.carMake')}
                                    placeholder={i18next.t('documentedit.carMake')}
                                />
                            )}
                        />
                    </Grid>
                </Grid>

                <Grid container alignItems="center" spacing={2}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Typography component="label" fontWeight={500}>{i18next.t('documentedit.documentType')}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 8 }}>
                        <Autocomplete
                            freeSolo
                            options={documentTypeOptions}
                            inputValue={documentType}
                            onInputChange={(_e, value) => setDocumentType(value)}
                            onChange={(_e, value) => setDocumentType(typeof value === 'string' ? value : '')}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    size="small"
                                    fullWidth
                                    label={i18next.t('documentedit.documentType')}
                                    placeholder={i18next.t('documentedit.documentType')}
                                />
                            )}
                        />
                    </Grid>
                </Grid>

                <Divider />

                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                        <Typography variant="body2" color="text.secondary">{i18next.t('common.lastupdated')}</Typography>
                        <Typography variant="body2">{document?.updateDateUTC ? moment.utc(document.updateDateUTC).local().format('L HH:mm') : ''}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 8 }}>
                        <Typography variant="body2" color="text.secondary">{i18next.t('common.updatedby')}</Typography>
                        <Typography variant="body2">{document?.lastUpdatedBy}</Typography>
                    </Grid>
                </Grid>

                <Divider />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    {!isAddMode && (
                        <Button
                            variant="contained"
                            color="error"
                            onClick={(e) => { e.preventDefault(); setShowDeleteModal(true); }}
                        >
                            {i18next.t('documentedit.delete')}
                        </Button>
                    )}
                    <Box sx={{ display: 'flex', gap: 1.5, ml: 'auto' }}>
                        <Button variant="contained" color="primary" onClick={saveUpdateDocument}>
                            {isAddMode ? i18next.t('documentadd.button') : i18next.t('documentedit.button')}
                        </Button>
                        <Button variant="outlined" color="inherit" onClick={(e) => { e.preventDefault(); setGoBack(true); }}>
                            {i18next.t('documentedit.close')}
                        </Button>
                    </Box>
                </Box>
            </Box>

            <DefaultPromptModal show={showDeleteModal} handleClose={() => setShowDeleteModal(false)} handleSubmit={handleSubmitDeleteModal}
                header={i18next.t('documentdelete.header')} body={i18next.t('documentdelete.body') + ' ' + (document ? document.title : '') + '?'}
                submitText={i18next.t('documentdelete.submittext')} closeText={i18next.t('documentdelete.closetext')} />
            <DefaultPromptModal show={showDeleteDoneModal} handleClose={() => setGoBack(true)} header={i18next.t('documentdelete.headerdone')}
                body={i18next.t('documentdelete.bodydone')} closeText={i18next.t('documentdelete.closetextdone')} />
        </Box>
    );
}

export default AdminDocumentView;
